import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Inject, Injectable } from "@angular/core";
import { Subject } from "rxjs";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";

import { ActiveUserAdapter } from "./adapter/ActiveUserAdapter";
import { ActiveUser } from "./model/ActiveUser";

import { CubiqBrokerClient, CUBIQ_BROKER } from "../../broker-client-services";
import { PromiseSet } from "../promise/PromiseSet";
import { PrimaryPromiseSet } from "../promise/PrimaryPromiseSet";
import { TRADABLE_ACCOUNTS, TRADABLE_CLIENTS } from "shared/tradable-services";

export const CHANNEL_ACTIVE_USER: string = "ActiveUserChannel";
export const ENCODABLE_TYPE_ERRORS: string = "com.cubiq.broker.Errors";

export interface IActiveUserProfileService {
  getActiveUserData(): Promise<ActiveUser>;
  setActiveUserData(userData: ActiveUser): Promise<any>;
  getUserUpdateSubject(): Subject<any>;
  isPrimed(): Promise<boolean>;
}

export abstract class ActiveUserProfilePromiseService implements IActiveUserProfileService {
  public abstract getActiveUserData(): Promise<ActiveUser>;
  public abstract setActiveUserData(userData: ActiveUser): Promise<any>;
  public abstract getUserUpdateSubject(): Subject<any>;
  public abstract isPrimed(): Promise<boolean>;
}

@Injectable()
export class ActiveUserProfilePrimaryService
  extends ActiveUserProfilePromiseService
  implements IActiveUserProfileService
{
  protected broker: ServiceMessageBroker;
  protected ports: Array<MessagePort> = new Array<MessagePort>();

  protected activeUser: ActiveUser;

  protected userUpdateEventSubject: Subject<any> = new Subject<any>();

  protected primed: PromiseSet<boolean> = new PrimaryPromiseSet<boolean>();

  constructor(
    @Inject(CUBIQ_BROKER) private cubiqBroker: CubiqBrokerClient,
    protected factory: ServiceMessageBrokerFactory,
    private activeUserAdapter: ActiveUserAdapter,
  ) {
    super();
    this.broker = this.factory.createMessageBroker(CHANNEL_ACTIVE_USER, true);
    this.broker.registerMethod(
      "getActiveUserData",
      [],
      this.getActiveUserData.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "setActiveUserData",
      [PRIMITIVE],
      this.setActiveUserData.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod("initPort", [PRIMITIVE], this.initPort.bind(this), null, false);
    this.broker.registerMethod(
      "getUserUpdateSubject",
      [],
      this.getUserUpdateSubject.bind(this),
      PRIMITIVE,
      false,
    );

    this.prime();
  }

  protected prime(): void {
    let profilePromise = new Promise<CubiqTS.StaticDataProfileImpl>((res) => {
      this.cubiqBroker
        .primeStaticData<CubiqTS.StaticDataProfileImpl>(CubiqTS.StaticDataProfileImpl.name)
        .subscribe((profile: CubiqTS.StaticDataProfileImpl) => {
          this.cubiqBroker
            .primeStaticData<string[]>(TRADABLE_ACCOUNTS)
            .subscribe((tradableAccountCodes: string[]) => {
              profile.tradableAccountCodes = tradableAccountCodes;
              this.cubiqBroker
                .primeStaticData<string[]>(TRADABLE_CLIENTS)
                .subscribe((tradableClientCodes: string[]) => {
                  profile.tradableClientCodes = tradableClientCodes;
                  let allClients: Array<CubiqTS.ClientImpl> = new Array<CubiqTS.ClientImpl>();
                  this.cubiqBroker
                    .primeStaticData<CubiqTS.ClientImpl[]>(CubiqTS.ClientImpl.name)
                    .subscribe(
                      (clients: CubiqTS.ClientImpl[]) => {
                        allClients = allClients.concat(clients);
                      },
                      (error: Error) => {
                        console.debug(`${this.constructor.name} clients prime failed %O`, error);
                      },
                      () => {
                        if (
                          Array.isArray(allClients) &&
                          allClients.length > 0 &&
                          (profile.clients === null ||
                            (Array.isArray(profile.clients) && profile.clients.length === 0))
                        ) {
                          profile.clients = allClients;
                        }
                        res(profile);
                      },
                    );
                });
            });
        });
    });

    profilePromise.then(
      (profile) => {
        this.activeUser = this.activeUserAdapter.transformActiveUser(profile);
        this.primed.resolve(true);
      },
      (error: Error) => {
        console.debug("sys settings prime failed %O", error);
        this.primed.resolve(false);
      },
    );
  }

  public initPort(port: MessagePort): void {
    this.ports.push(port);
  }

  public getActiveUserData(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.primed.get().then(
        (value: boolean) => {
          if (value) {
            resolve(this.activeUser);
          } else {
            reject("ERROR: getActiveUserData() false prime!");
          }
        },
        (reason) => {
          logger.out.error(`getActiveUserData() promise rejection:` + reason);

          reject(reason);
        },
      );
    });
  }

  public setActiveUserData(userData: any): Promise<any> {
    this.activeUser = userData;
    this.userUpdateEventSubject.next(this.activeUser);
    this.ports.forEach((port) => {
      port.postMessage(this.activeUser);
    });
    return new Promise<void>((resolve, reject) => {
      resolve();
    });
  }

  public getUserUpdateSubject(): Subject<any> {
    return this.userUpdateEventSubject;
  }

  public isPrimed(): Promise<boolean> {
    return this.primed.get();
  }
}
